"use client";

import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DashboardStats } from "@/components/dashboard-stats";
import { RecentOrders } from "@/components/recent-orders";
import { PrintBatches } from "@/components/print-batches";
import { QcStats } from "@/components/qc-stats";
import { CollapsibleSidebar } from "@/components/collapsible-sidebar";
import useAuth from "@/hooks/auth/useAuth";
import Link from "next/link";
import { Printer, Package, BarChart3, ChevronRight, Loader2 } from "lucide-react";

export default function HomePage() {
  const { user, loading } = useAuth();

  // Show loading state while auth is loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // User is authenticated and has account access, show dashboard

  return (
    <div className="min-h-screen bg-background">
      {/* Collapsible Sidebar */}
      <CollapsibleSidebar />

      {/* Main content */}
      <div className="min-h-screen pl-16">
        <header className="flex h-16 items-center gap-4 border-b bg-white px-6">
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Dashboard</h1>
            {user && (
              <p className="text-sm text-muted-foreground">
                Welcome, {user.user_metadata?.first_name || user.name?.split(" ")[0] || user.name || "User"}!
              </p>
            )}
          </div>
        </header>

        <main className="p-6">
          {/* Navigation Tiles */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            <Link href="/orders">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Package className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Orders</h3>
                        <p className="text-sm text-gray-600">Manage orders</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link href="/batches">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Printer className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Print Batches</h3>
                        <p className="text-sm text-gray-600">Create and manage batches</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link href="/inventory">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-orange-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-orange-100 rounded-lg">
                        <BarChart3 className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Inventory</h3>
                        <p className="text-sm text-gray-600">Check stock levels</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>

          <div className="mb-6">
            <DashboardStats />
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 mb-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Orders</CardTitle>
                <CardDescription>Latest orders from ShipHero</CardDescription>
              </CardHeader>
              <CardContent>
                <RecentOrders />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Active Print Batches</CardTitle>
                <CardDescription>Current batches in production</CardDescription>
              </CardHeader>
              <CardContent>
                <PrintBatches />
              </CardContent>
            </Card>
          </div>

          <QcStats />
        </main>
      </div>
    </div>
  );
}
